import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties, MouseEvent } from 'react'
import type {
  HealthState,
  PublicShowcaseApplication,
  PublicShowcaseSnapshot,
} from '../shared/types'
import { commitIntensity } from '../shared/repository'
import { MONITOR_REFRESH_INTERVAL_MS } from '../shared/timing'
import { api } from './api'
import { fullDate, relativeTime } from './format'
import { PreviewImage } from './components'
import {
  SHOWCASE_BASE,
  formatMetric,
  heroPreview,
  hostOf,
  productPath,
  cardLine,
  readRoute,
  sellableMetrics,
} from './showcase-data'
import {
  FeaturedReel,
  Marquee,
  ShowcaseVideo,
} from './showcase-motion'
import { HOUSTON_TIERSEL_DEMO, showcaseDemoFor } from './hackathon-demos'
import type { ShowcaseDemo } from './hackathon-demos'
import { fleetTotals } from '../shared/fleet-totals'
import './showcase.css'

/**
 * How many products lead the page.
 *
 * Computed, not editorial. The payload arrives ranked by effort — capped,
 * recency-weighted commits multiplied by how much of the project is happening
 * this week — so the front of the page is wherever the work is actually going,
 * and it re-orders itself as that changes.
 *
 * A hand-kept list was the alternative and it went stale twice: it still named
 * the products chosen months earlier while the repositories said the effort had
 * moved on. A build log whose front page disagrees with its own commit history
 * is the one thing it cannot afford to be.
 *
 * Six keeps the pinned reel to a few screens of scroll.
 */
const FEATURED_COUNT = 6

/** Enough captures to loop across a wide screen without decoding the whole fleet on a phone. */
const MARQUEE_COUNT = 16

const STATUS_LABEL: Record<HealthState, string> = {
  operational: 'Available',
  degraded: 'Degraded',
  outage: 'Unavailable',
  maintenance: 'Maintenance',
  unknown: 'Checking',
}

type ViewTransitionDocument = Document & {
  startViewTransition?: (callback: () => void) => unknown
}

function withTransition(apply: () => void) {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const doc = document as ViewTransitionDocument
  if (reduced || typeof doc.startViewTransition !== 'function') {
    apply()
    return
  }
  doc.startViewTransition(apply)
}

/** Morph the card preview into the detail hero where the browser supports it. */
function navigate(to: string, apply: () => void) {
  window.history.pushState({}, '', to)
  withTransition(apply)
}

export function PublicShowcase() {
  const [snapshot, setSnapshot] = useState<PublicShowcaseSnapshot>()
  const [error, setError] = useState('')
  const [route, setRoute] = useState(() => readRoute(window.location.pathname))
  const mainRef = useRef<HTMLDivElement>(null)
  const firstRender = useRef(true)

  useEffect(() => {
    const load = async () => {
      try {
        setSnapshot(await api.showcase())
        setError('')
      } catch {
        setError('Could not reach the live data just now. Trying again shortly.')
      }
    }
    void load()
    const poll = window.setInterval(load, MONITOR_REFRESH_INTERVAL_MS)
    return () => window.clearInterval(poll)
  }, [])

  useEffect(() => {
    document.body.classList.add('sc-page')
    return () => document.body.classList.remove('sc-page')
  }, [])

  useEffect(() => {
    const onPop = () =>
      withTransition(() => setRoute(readRoute(window.location.pathname)))
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  const go = useCallback((to: string) => {
    navigate(to, () => {
      setRoute(readRoute(new URL(to, window.location.origin).pathname))
      window.scrollTo(0, 0)
    })
  }, [])

  /**
   * A client-side route change is invisible to assistive technology: the URL
   * moves, the document does not, and focus stays wherever it was — which,
   * after activating a link that was then removed, is <body>. The next Tab
   * restarts from the top of the page and nothing has been announced.
   *
   * So move focus to the new view and say what happened. Skipped on the first
   * render, where the browser's own page load already did both.
   */
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false
      return
    }
    mainRef.current?.focus()
  }, [route])

  const applications = snapshot?.applications ?? []
  const active = route
    ? applications.find((application) => application.id === route)
    : undefined

  // The document title is the thing a shared link shows in a tab and a
  // bookmark, so it has to follow the route even though the server rendered
  // the index head.
  useEffect(() => {
    if (!snapshot) return
    document.title = active
      ? `${active.name} — built and run by Shah`
      : `Shah’s build log: ${snapshot.applications.length} products running in production`
  }, [active, snapshot])

  if (error && !snapshot) {
    return (
      <div className="sc">
        <Nav onHome={go} />
        <main className="sc-container sc-section">
          <p className="sc-note">{error}</p>
        </main>
      </div>
    )
  }

  if (!snapshot) {
    return (
      <div className="sc">
        <Nav onHome={go} />
        <main className="sc-container sc-section">
          <p className="sc-note">Reading the live signal…</p>
        </main>
      </div>
    )
  }

  if (route && !active) {
    return (
      <div className="sc">
        <Nav onHome={go} />
        <main className="sc-container sc-section">
          <p className="sc-note">
            No product at this address.{' '}
            <a href={SHOWCASE_BASE() || '/'}>See all of them</a>.
          </p>
        </main>
      </div>
    )
  }

  return (
    <div className="sc">
      <a className="sc-skip" href="#sc-main">
        Skip to content
      </a>
      <Nav onHome={go} />
      <div id="sc-main" ref={mainRef} tabIndex={-1}>
        {active ? (
          <Detail application={active} applications={applications} onGo={go} />
        ) : (
          <Index snapshot={snapshot} onGo={go} />
        )}
      </div>
      <p className="sc-sr" role="status" aria-live="polite">
        {active ? `${active.name}. Product page.` : 'All products.'}
      </p>
      <Footer />
    </div>
  )
}

function Nav({ onHome }: { onHome: (to: string) => void }) {
  const home = SHOWCASE_BASE() || '/'
  return (
    <header className="sc-nav">
      <div className="sc-container sc-nav-inner">
        <a
          className="sc-brand"
          href={home}
          onClick={(event) => {
            if (isPlainClick(event)) {
              event.preventDefault()
              onHome(home)
            }
          }}
        >
          Shah <span>Build log</span>
        </a>
        <nav className="sc-nav-links">
          <a href={`${home}#work`}>
            <span className="sc-nav-full">The work</span>
            <span className="sc-nav-short">Work</span>
          </a>
          <a href={`${home}#build`}>
            <span className="sc-nav-full">Work with me</span>
            <span className="sc-nav-short">Hire me</span>
          </a>
          <a
            className="sc-nav-keep"
            href="https://health.sasid.ai"
            target="_blank"
            rel="noreferrer"
          >
            <span className="sc-nav-full">Live monitor ↗</span>
            <span className="sc-nav-short">Monitor ↗</span>
          </a>
        </nav>
      </div>
    </header>
  )
}

function isPlainClick(event: MouseEvent) {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  )
}

function Index({
  snapshot,
  onGo,
}: {
  snapshot: PublicShowcaseSnapshot
  onGo: (to: string) => void
}) {
  const applications = snapshot.applications
  const counts = useMemo(() => {
    const totals = fleetTotals(applications)
    return { ...totals, total: totals.published, operational: totals.up }
  }, [applications])

  const featured = applications.slice(0, FEATURED_COUNT)
  const rest = applications.slice(FEATURED_COUNT)

  return (
    <main>
      <section className="sc-container sc-hero">
        <p className="sc-eyebrow sc-hero-eyebrow">
          <span className="sc-live">
            <i />
            Live
          </span>
          The build log
        </p>
        <h1 className="sc-display sc-hero-title">
          {counts.total} products,{' '}
          <br className="sc-wide-only" />
          built and <em>still running</em>.
        </h1>
        <dl className="sc-stats">
          <div className="sc-stat-cell">
            <dt>Published</dt>
            <dd>{counts.total}</dd>
          </div>
          <div className="sc-stat-cell">
            <dt>Up right now</dt>
            <dd>{counts.operational}</dd>
            {(counts.down > 0 || counts.flagged > 0) && (
              <dd className="sc-stat-note" data-down={counts.down > 0}>
                {[
                  counts.down > 0 ? `${counts.down} down` : '',
                  counts.flagged > 0 ? `${counts.flagged} degraded` : '',
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </dd>
            )}
          </div>
          <div className="sc-stat-cell">
            <dt>Commits · 30 days</dt>
            <dd>{counts.commits30d.toLocaleString('en-US')}</dd>
          </div>
          <div className="sc-stat-cell">
            <dt>Pages watched</dt>
            <dd>{counts.pagesWatched}</dd>
          </div>
        </dl>
        <p className="sc-checked">
          Checked {relativeTime(snapshot.observedAt)} · DNS, certificate, page
          and health contract · every 5 minutes
        </p>
        <p className="sc-lede sc-hero-lede">
          One engineer designed, built, deployed and now runs every product on
          this page. The numbers above are read from the monitor, not typed
          into it — which is why they include the failures.
        </p>
        <div className="sc-hero-actions">
          <a className="sc-btn sc-btn-primary" href="#work">
            See the fleet
          </a>
          <a className="sc-btn" href="#build">
            Work with me
          </a>
        </div>
      </section>

      <Marquee applications={applications.slice(0, MARQUEE_COUNT)} onGo={onGo} />

      <FeaturedReel
        applications={featured}
        onGo={onGo}
        statusFor={(application) =>
          application.status !== 'operational' ? (
            <span
              className={`sc-dot sc-dot-${application.status}`}
              title={STATUS_LABEL[application.status]}
            />
          ) : null
        }
        header={
          <div className="sc-section-head sc-section-head-flush">
            <div>
              <p className="sc-eyebrow">01 — Start here</p>
              <h2 className="sc-h2">Where the work is going</h2>
              <p>
                Anything new under heavy development leads, then the six under
                the heaviest work right now — read from the commit history, not
                picked by me.
              </p>
            </div>
          </div>
        }
      />

      <section className="sc-container sc-section">
        <div className="sc-section-head">
          <div>
            <p className="sc-eyebrow">02 — The fleet</p>
            <h2 className="sc-h2">Everything else, and its current state</h2>
            <p>
              Explore the products, watch their demos and see what is being
              built.
            </p>
          </div>
          <span className="sc-section-count">{rest.length} products</span>
        </div>
        <Fleet applications={rest} onGo={onGo} />
      </section>

      <section className="sc-container sc-section" id="build">
        <div className="sc-band">
          <p className="sc-eyebrow">Work with me</p>
          <h2 className="sc-h2" style={{ marginTop: 'var(--sp-4)' }}>
            The rails these were built on are the ones yours gets.
          </h2>
          <p className="sc-lede">
            Domain, certificate, database, deploy path, health contract and
            monitoring exist before the first feature does — because that is the
            part that stalls builds. It is the same platform every product here
            runs on.
          </p>
          <div className="sc-band-actions">
            <a className="sc-btn sc-btn-primary" href="https://sasid.ai">
              See how an engagement works
            </a>
            <a
              className="sc-btn"
              href="https://health.sasid.ai"
              target="_blank"
              rel="noreferrer"
            >
              Inspect the monitor first ↗
            </a>
          </div>
          <div className="sc-paths">
            <div className="sc-path">
              <h3>For investors</h3>
              <p>
                The interesting number is not {counts.total}. It is that the
                next one costs the same as the last one, because they all run on
                one platform, one deploy path and one health contract.
              </p>
              <a href="https://sasid.ai">Ask for the build-log register →</a>
            </div>
            <div className="sc-path">
              <h3>For clients</h3>
              <p>
                You need one thing built, and it needs to actually run. The part
                that usually stalls is already solved here, {counts.total}{' '}
                times.
              </p>
              <a href="https://sasid.ai">See what it takes to build one →</a>
            </div>
            <div className="sc-path">
              <h3>For buyers</h3>
              <p>
                Some of these are available. You would be buying a running
                product — repository, domain, deployment, health contract and
                the operating history, so you can see exactly what you are
                taking on.
              </p>
              <a href="https://sasid.ai">Ask what transfers →</a>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

function Fleet({
  applications,
  onGo,
}: {
  applications: PublicShowcaseApplication[]
  onGo: (to: string) => void
}) {
  return (
    <div className="sc-fleet-grid">
      {applications.map((application) => (
        <ApplicationCard
          key={application.id}
          application={application}
          onGo={onGo}
        />
      ))}
    </div>
  )
}

function ApplicationMedia({
  application,
  demo,
  className,
}: {
  application: PublicShowcaseApplication
  demo?: ShowcaseDemo
  className: string
}) {
  if (demo) {
    return (
      <ShowcaseVideo
        demo={demo}
        alt={`${application.name} demo preview`}
        className={className}
      />
    )
  }
  const preview = heroPreview(application)
  if (preview) {
    return (
      <img
        className={className}
        src={preview.screenshot.imageUrl}
        alt={`${application.name} — ${preview.screenshot.pageTitle ?? preview.name}`}
        width={preview.screenshot.width}
        height={preview.screenshot.height}
        loading="lazy"
        decoding="async"
      />
    )
  }
  if (application.iconUrl) {
    return (
      <img
        className={`${className} sc-card-icon`}
        src={application.iconUrl}
        alt=""
        loading="lazy"
        decoding="async"
        style={{
          objectFit: application.iconFit ?? 'contain',
          objectPosition: application.iconPosition ?? 'center',
        }}
      />
    )
  }
  return (
    <span className="sc-card-monogram" aria-hidden="true">
      {application.name
        .split(/\s+/)
        .map((word) => word[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()}
    </span>
  )
}

function ShowcaseLinks({
  application,
  demo,
}: {
  application: PublicShowcaseApplication
  demo?: ShowcaseDemo
}) {
  return (
    <nav className="sc-card-links" aria-label={`${application.name} links`}>
      {demo ? (
        <>
          <a href={demo.demo} target="_blank" rel="noreferrer">
            Demo ↗
          </a>
          {demo.pitch && (
            <a href={demo.pitch} target="_blank" rel="noreferrer">
              Deck ↗
            </a>
          )}
          <a href={demo.app} target="_blank" rel="noreferrer">
            Live ↗
          </a>
          {demo.devpost && (
            <a href={demo.devpost} target="_blank" rel="noreferrer">
              Devpost ↗
            </a>
          )}
          {application.id === 'jevvy' && (
            <a
              href={HOUSTON_TIERSEL_DEMO.devpost}
              target="_blank"
              rel="noreferrer"
            >
              Houston Devpost ↗
            </a>
          )}
        </>
      ) : (
        <a href={application.homepage} target="_blank" rel="noreferrer">
          Live ↗
        </a>
      )}
    </nav>
  )
}

function DemoFilm({ demo, name }: { demo: ShowcaseDemo; name: string }) {
  const dialog = useRef<HTMLDialogElement>(null)
  const video = useRef<HTMLVideoElement>(null)

  const close = () => {
    const node = dialog.current
    if (!node) return
    if (node.open) node.close()
    else {
      node.removeAttribute('open')
      document.dispatchEvent(new Event('showcase-film-close'))
    }
  }

  useEffect(
    () => () => {
      document.dispatchEvent(new Event('showcase-film-close'))
    },
    [],
  )

  const open = () => {
    const node = dialog.current
    if (!node) return
    document.dispatchEvent(new Event('showcase-film-open'))
    if (typeof node.showModal === 'function') node.showModal()
    else node.setAttribute('open', '')
    const player = video.current
    if (player) {
      player.src = demo.film
      player.load()
      void player.play().catch(() => {})
    }
  }

  return (
    <>
      <button className="sc-card-watch" type="button" onClick={open}>
        Watch full film
      </button>
      <dialog
        ref={dialog}
        className="sc-film-dialog"
        aria-labelledby={`${demo.slug}-film-title`}
        onClose={() => {
          const player = video.current
          if (player) {
            player.pause()
            player.removeAttribute('src')
            player.load()
          }
          document.dispatchEvent(new Event('showcase-film-close'))
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) close()
        }}
      >
        <div className="sc-film-dialog-inner">
          <div className="sc-film-dialog-head">
            <div>
              <p className="sc-eyebrow">Demo film</p>
              <h2 id={`${demo.slug}-film-title`} className="sc-h2">
                {name}
              </h2>
            </div>
            <button
              className="sc-film-dialog-close"
              type="button"
              onClick={close}
              aria-label="Close demo film"
            >
              ×
            </button>
          </div>
          <video
            ref={video}
            className="sc-film-dialog-video"
            poster={demo.poster}
            controls
            playsInline
            preload="none"
          />
          <div className="sc-film-dialog-foot">
            <span>
              {demo.durationSeconds > 0
                ? `${Math.round(demo.durationSeconds / 60)} min demo`
                : 'Full demo film'}
            </span>
            <a href={demo.demo} target="_blank" rel="noreferrer">
              Open demo ↗
            </a>
          </div>
        </div>
      </dialog>
    </>
  )
}

function ApplicationCard({
  application,
  onGo,
}: {
  application: PublicShowcaseApplication
  onGo: (to: string) => void
}) {
  const demo = showcaseDemoFor(application)
  const to = productPath(application.id)
  return (
    <article
      className="sc-app-card"
      data-state={application.status}
      style={{ '--accent': application.accent ?? '#c8a664' } as CSSProperties}
    >
      <a
        className="sc-app-card-main"
        href={to}
        onClick={(event) => {
          if (!isPlainClick(event)) return
          event.preventDefault()
          onGo(to)
        }}
      >
        <div className="sc-card-visual">
          <ApplicationMedia
            application={application}
            demo={demo}
            className="sc-card-media"
          />
          <span className="sc-card-visual-shade" aria-hidden="true" />
          <span className="sc-card-badge">
            {demo ? 'Demo film' : heroPreview(application) ? 'Live capture' : 'Product'}
          </span>
        </div>
        <div className="sc-app-card-body">
          <div className="sc-app-card-kicker">
            <span>
              {demo?.challenge
                ? `Challenge ${String(demo.challenge).padStart(2, '0')}`
                : demo?.devpost
                  ? 'Featured prototype'
                  : demo
                    ? 'Interactive demo'
                    : hostOf(application)}
            </span>
            {application.status !== 'operational' && (
              <span className={`sc-dot sc-dot-${application.status}`} title={STATUS_LABEL[application.status]} />
            )}
          </div>
          <h3>{application.name}</h3>
          <p>{demo?.tagline ?? cardLine(application.description)}</p>
        </div>
      </a>
      <div className="sc-app-card-footer">
        <ShowcaseLinks application={application} demo={demo} />
        {demo && <DemoFilm demo={demo} name={application.name} />}
      </div>
    </article>
  )
}

function Detail({
  application,
  applications,
  onGo,
}: {
  application: PublicShowcaseApplication
  applications: PublicShowcaseApplication[]
  onGo: (to: string) => void
}) {
  const [frame, setFrame] = useState(0)
  useEffect(() => setFrame(0), [application.id])

  const previews = application.previews
  const shot = previews[Math.min(frame, previews.length - 1)]
  const metrics = sellableMetrics(application)
  const activity = application.activity
  const demo = showcaseDemoFor(application)
  const home = SHOWCASE_BASE() || '/'
  const position = applications.findIndex(
    (candidate) => candidate.id === application.id,
  )
  const next = applications[(position + 1) % applications.length]

  return (
    <main className="sc-container sc-detail">
      <a
        className="sc-back"
        href={home}
        onClick={(event) => {
          if (!isPlainClick(event)) return
          event.preventDefault()
          onGo(home)
        }}
      >
        ← All products
      </a>

      <div className="sc-detail-head">
        <p className="sc-eyebrow">
          {hostOf(application)} · {STATUS_LABEL[application.status]}
        </p>
        <h1 className="sc-display" style={{ marginTop: 'var(--sp-5)' }}>
          {application.name}
        </h1>
        <p className="sc-lede">{application.description}</p>
        <div className="sc-detail-actions">
          <a
            className="sc-btn sc-btn-primary"
            href={demo?.app ?? application.homepage}
            target="_blank"
            rel="noreferrer"
          >
            Visit the live product ↗
          </a>
          {demo && <ShowcaseLinks application={application} demo={demo} />}
          {demo && <DemoFilm demo={demo} name={application.name} />}
          <a className="sc-btn" href="https://sasid.ai">
            Enquire about this one
          </a>
        </div>
      </div>

      {shot && (
        <>
          <figure
            className="sc-detail-shot"
            style={{ viewTransitionName: 'sc-hero-shot' } as CSSProperties}
          >
            <PreviewImage
              src={shot.screenshot.imageUrl}
              alt={`${application.name} — ${
                shot.screenshot.pageTitle ?? shot.name
              }`}
            />
          </figure>
          {previews.length > 1 && (
            <div className="sc-filmstrip">
              {previews.map((preview, index) => (
                <button
                  key={preview.id}
                  type="button"
                  aria-current={index === frame}
                  aria-label={preview.name}
                  onClick={() => setFrame(index)}
                >
                  <img src={preview.screenshot.imageUrl} alt="" />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      <div className="sc-detail-grid">
        <Stat label="Status" value={STATUS_LABEL[application.status]} />
        <Stat
          label="Paths watched"
          value={`${previews.length} · every 5 min`}
        />
        {activity?.commits30d ? (
          <Stat label="Commits · 30 days" value={String(activity.commits30d)} />
        ) : null}
        {application.latestDeploymentAt ? (
          <Stat
            label="Last deploy"
            value={relativeTime(application.latestDeploymentAt)}
          />
        ) : null}
      </div>

      {metrics.length > 0 && (
        <>
          <h2 className="sc-h2" style={{ marginTop: 'var(--sp-9)' }}>
            Real usage
          </h2>
          <div className="sc-detail-grid" style={{ marginTop: 'var(--sp-5)' }}>
            {metrics.map((metric) => (
              <Stat
                key={metric.id}
                label={metric.name}
                value={formatMetric(metric.value as number, metric.unit)}
              />
            ))}
          </div>
        </>
      )}

      {activity && activity.daily.length > 0 && (
        <>
          <h2 className="sc-h2" style={{ marginTop: 'var(--sp-9)' }}>
            Build record
          </h2>
          <p className="sc-lede" style={{ marginTop: 'var(--sp-4)' }}>
            Commits per day for the last thirty days
            {activity.lastCommitAt
              ? `, most recent ${relativeTime(activity.lastCommitAt)}`
              : ''}
            .
          </p>
          <div className="sc-activity">
            {activity.daily.map((count, index) => (
              <i
                key={index}
                title={`${count} commit${count === 1 ? '' : 's'}`}
                style={
                  {
                    '--act': `rgba(200, 166, 100, ${
                      [0.06, 0.22, 0.4, 0.62, 0.85][commitIntensity(count)]
                    })`,
                  } as CSSProperties
                }
              />
            ))}
          </div>
        </>
      )}

      <h2 className="sc-h2" style={{ marginTop: 'var(--sp-9)' }}>
        Built with
      </h2>
      <div className="sc-chips">
        {application.technologies.map((technology) => (
          <span className="sc-chip" key={technology.name}>
            {technology.name}
          </span>
        ))}
      </div>
      {shot && (
        <p className="sc-eyebrow" style={{ marginTop: 'var(--sp-6)' }}>
          Screenshot captured {fullDate(shot.screenshot.capturedAt)}
        </p>
      )}

      <div className="sc-band">
        <p className="sc-eyebrow">Next</p>
        <h2 className="sc-h2" style={{ marginTop: 'var(--sp-4)' }}>
          {next.name}
        </h2>
        <p className="sc-lede">{next.description}</p>
        <div className="sc-band-actions">
          <a
            className="sc-btn"
            href={productPath(next.id)}
            onClick={(event) => {
              if (!isPlainClick(event)) return
              event.preventDefault()
              onGo(productPath(next.id))
            }}
          >
            Open {next.name} →
          </a>
        </div>
      </div>
    </main>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="sc-stat">
      <span className="sc-stat-value">{value}</span>
      <span className="sc-stat-label">{label}</span>
    </div>
  )
}

function Footer() {
  return (
    <footer className="sc-footer">
      <div className="sc-container sc-footer-inner">
        <span>One engineer, one platform, one monitor anyone can read.</span>
        <span>
          <a href="https://sasid.ai">sasid.ai</a> ·{' '}
          <a href="https://health.sasid.ai">health.sasid.ai</a>
        </span>
      </div>
    </footer>
  )
}

const WORDS = [
  'Zero',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
]

/** Display type reads better with the count spelled out; numerals stay in the proof strip. */
function numberWord(value: number): string {
  if (value <= 10) return WORDS[value]
  const tens = [
    '',
    '',
    'Twenty',
    'Thirty',
    'Forty',
    'Fifty',
    'Sixty',
    'Seventy',
    'Eighty',
    'Ninety',
  ]
  if (value < 100) {
    const unit = value % 10
    const base = tens[Math.floor(value / 10)]
    return unit === 0 ? base : `${base}-${WORDS[unit].toLowerCase()}`
  }
  return String(value)
}
